import { isEmpty } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import {
  SectionContainer,
  SectionDivider,
} from "@app/analytics/components/reports/styles";
import EmptyTab from "@app/analytics/MeetingReport/components/EmptyTab";
import useMeetingReportSectionScroll from "@app/analytics/MeetingReport/hooks/useMeetingReportSectionScroll";
import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useRecapCharts from "@app/analytics/MeetingReport/hooks/useRecapCharts";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import { MainContentContainer } from "@app/analytics/MeetingReport/styles";
import type { RecapSection } from "@app/analytics/MeetingReport/types";
import { getSummaryCopyContent } from "@app/analytics/MeetingReport/utils/transcriptUtils";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import { usePageTitle } from "@app/utils/hooks";

import { Column } from "@rui/Stack";

import EmptyRecap from "./EmptyRecap";
import MeetingReportRecapCharts from "./MeetingReportRecapCharts";
import MeetingReportRecapSubnav from "./MeetingReportRecapSubnav";
import ScrollableRecapContent from "./ScrollableRecapContent";
import { EmptyContentChartsWrapper } from "./styles";
import TabbedRecapContent from "./TabbedRecapContent";

const MeetingReportRecapContent = ({
  isNotetakerReport = false,
}: {
  isNotetakerReport?: boolean;
}) => {
  usePageTitle("Meeting Report - Recap", "Reports");
  const [queryParams] = useSearchParams();

  const { inDesktopView } = useResponsiveView();
  const { isEmpty: transcriptIsEmpty, summary } = useTranscript();
  const { showCharts, ...chartProps } = useRecapCharts();

  const {
    data: {
      currentUserCanUpgradeForReport,
      isRestrictedReport,
      shouldUpsellTranscriptEntities,
    },
  } = useSessionMetadata();

  const preselectedSection = queryParams.get("section") as RecapSection;

  const { notes: sessionNotes, isInitialLoading: isNotesInitialLoading } =
    useMeetingReportSessionNotes({
      enabled: !isRestrictedReport,
    });

  const [selectedSection, setSelectedSection] =
    useMeetingReportSectionScroll<RecapSection>({
      defaultSection: "notes",
      preselectedSection,
      enabled: !transcriptIsEmpty,
      sections: ["notes", "transcript"],
    });

  const hasNoContent =
    transcriptIsEmpty &&
    !isNotesInitialLoading &&
    isEmpty(sessionNotes) &&
    !isNotetakerReport;

  if (isRestrictedReport && !currentUserCanUpgradeForReport) {
    return (
      <EmptyTab
        tab="Recap"
        title="Get unlimited reports"
        canUpgradeCurrentReport={currentUserCanUpgradeForReport}
      />
    );
  }

  if (hasNoContent && !shouldUpsellTranscriptEntities) {
    return (
      <>
        {showCharts && (
          <>
            <SectionContainer>
              <EmptyContentChartsWrapper>
                <MeetingReportRecapCharts {...chartProps} />
              </EmptyContentChartsWrapper>
            </SectionContainer>
            <SectionDivider />
          </>
        )}
        <SectionContainer sx={{ py: 2, display: "flex", flex: 1 }}>
          <EmptyRecap />
        </SectionContainer>
      </>
    );
  }

  return (
    <>
      <MeetingReportRecapSubnav
        copyContent={getSummaryCopyContent(
          summary,
          sessionNotes,
          isNotetakerReport
        )}
        isNotetakerReport={isNotetakerReport}
        isRestrictedReport={isRestrictedReport}
        onSectionSelect={setSelectedSection}
        selectedSection={selectedSection}
      />
      <MainContentContainer>
        {inDesktopView ? (
          <ScrollableRecapContent
            chartProps={chartProps}
            showCharts={showCharts}
          />
        ) : (
          <Column flex={1}>
            <TabbedRecapContent
              chartProps={chartProps}
              selectedSection={selectedSection}
              showCharts={showCharts}
            />
          </Column>
        )}
      </MainContentContainer>
    </>
  );
};

export default MeetingReportRecapContent;
